import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useContext, useMemo } from 'react'
import { useAppStore } from 'v2/state/app'
import type { RPermissions } from '../../pages/settings/types'
import type { ModulePermissionTypeMap, RbacModules, RbacPermissionTypes } from '../../types/rbac'
import { SuspenseLoader } from '../components/suspense-loader'
import { settingsService } from '../services/settings-service'
import { isEmbedMode } from '../utils/embed-session'
import { getSettingsQueryKeys } from '../utils/query-key-management'
import { PermissionsContext } from './permissions-context'

/**
 * Transforms the permissions data from the API into a map of modules and their permission types
 * such as:
 * {
 *   "user": {
 *     "view": true,
 *     "edit": true,
 *     "delete": true
 *   }
 *  ...other modules
 * }
 * @param permissions - The permissions data from the API
 * @returns A map of modules and their permission types
 */
export const transformPermissionsData = (permissions: RPermissions): ModulePermissionTypeMap => {
  if (!permissions) return {}
  const permissionMap: ModulePermissionTypeMap = {}
  Object.entries(permissions.permissions).forEach(([module, { permission_levels }]) => {
    permissionMap[module as RbacModules] = {}
    Object.entries(permission_levels).forEach(([type, level]) => {
      permissionMap[module as RbacModules]![type as RbacPermissionTypes] = level !== undefined
    })
  })
  return permissionMap
}

/**
 * Fetches the permissions data from the API and transforms it into a map of modules and their permission types
 * @returns A map of modules and their permission types
 */
export const useGetPermissionsQuery = () => {
  const isAuthenticated = useAppStore((state) => state.userAuthenticated)
  return useQuery({
    queryKey: getSettingsQueryKeys().permissions,
    queryFn: async () => {
      const response = await settingsService.getPermissions()
      return response.data
    },
    select: (data) => transformPermissionsData(data),
    refetchOnMount: 'always',
    // Never in embed: permissions come from StaticPermissionsProvider (not allow-listed).
    enabled: isAuthenticated && !isEmbedMode(),
  })
}

export interface PermissionsProviderProps {
  children: ReactNode
}

export const usePermissions = () => useContext(PermissionsContext)

// A fixed permissions map for the embed iframe, built from the session's read/edit.
export const StaticPermissionsProvider = ({
  permissions,
  children,
}: PermissionsProviderProps & { permissions: ModulePermissionTypeMap }) => {
  const value = useMemo(
    () => ({
      hasPermission: (module: string, permissionType: string) =>
        permissions?.[module as RbacModules]?.[permissionType as RbacPermissionTypes] ?? false,
      permissions,
    }),
    [permissions],
  )
  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
}

export const PermissionsProvider = ({ children }: PermissionsProviderProps) => {
  const { data, isFetching } = useGetPermissionsQuery()

  const value = useMemo(
    () => ({
      hasPermission: (module: string, permissionType: string) =>
        data?.[module as RbacModules]?.[permissionType as RbacPermissionTypes] ?? false,
      permissions: data ?? {},
    }),
    [data],
  )
  if (isFetching) return <SuspenseLoader text="Fetching account details..." />

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
}
